import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { EmployeeCompensationSummarySection } from "@/pages/employees/employee/components/EmployeeCompensationSummarySection.tsx";
import { EmployeePageSection } from "@/pages/employees/employee/employee-page.constants.ts";
import { getEmployeeTimeOffCategoryCaption } from "@/pages/employees/employee/components/employee-time-off-caption.helper.ts";
import { useSearchEmployeeReviewParticipantViews } from "@/queries/employees/use-employee-performance-review.query.ts";
import { useEmployeeTimeOffCategoriesData } from "@/queries/employees/use-employees.query.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiEmployeeLifecycleStatus } from "@/types/employees/employee.types.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { Box, ButtonLink, FontAwesomeIcon, ListItem, Stack, Title } from "@skailer-com/uikit";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface SummaryRowItem {
  id: string;
  title: string;
  caption?: string;
  captionSlot?: ReactNode;
  valueSlot?: ReactNode;
  emoji?: string;
}

interface SummarySectionCardProps {
  title: string;
  caption?: string;
  items: SummaryRowItem[];
  link: string;
  viewFullTitle: string;
}

function SummarySectionCard({
  title,
  caption,
  items,
  link,
  viewFullTitle,
}: SummarySectionCardProps) {
  return (
    <Box variant={"border"}>
      <Stack className={"px-2"} gap={"md"}>
        <Title header={title} caption={caption} size={"md"} />

        <Stack gap={"sm"}>
          {items.map((item, index) => (
            <Stack key={item.id} gap={"sm"}>
              <ListItem
                title={item.title}
                caption={item.caption}
                captionSlot={item.captionSlot}
                valueSlot={item.valueSlot}
                emoji={item.emoji}
                forceShowAvatar={!!item.emoji}
                className={index === items.length - 1 ? "border-b-0" : undefined}
              />
            </Stack>
          ))}
        </Stack>

        <ButtonLink
          href={link}
          variant={"secondary"}
          rightIcon={<FontAwesomeIcon icon={"fa-light fa-angle-right"} />}
          className={"w-fit"}
        >
          {viewFullTitle}
        </ButtonLink>
      </Stack>
    </Box>
  );
}

export function EmployeePageOverviewSummary({ employee }: EmployeePageComponentProps) {
  const { t } = useTranslation();
  const { canViewField, canViewAnyOfFields, canIEdit, isFeatureEnabled } = usePermissions();
  const { renderShortDate } = useFormatter();
  const [timeOffPayload] = useState({ date_balance: new Date() });

  const canViewCompensationSummary = canViewAnyOfFields(employee, [
    EmployeeFieldPermission.employee_compensation_base_salary_gross,
    EmployeeFieldPermission.employee_compensation_base_salary_net,
    EmployeeFieldPermission.employee_compensation_total_cost,
    EmployeeFieldPermission.employee_compensation_lines,
    EmployeeFieldPermission.employee_compensation_date,
  ]);
  const canViewEmploymentType = canViewField(
    employee,
    EmployeeFieldPermission.employee_employment_employment_type
  );
  const canViewEmploymentContract = canViewField(
    employee,
    EmployeeFieldPermission.employee_employment_contract
  );
  const canViewPosition = canViewField(
    employee,
    EmployeeFieldPermission.employee_position_job_title
  );

  const canViewTimeOff =
    isFeatureEnabled(ApiFeatureType.time_off) &&
    (canIEdit(ApiPermission.manage_leave_policies) ||
      canViewField(employee, EmployeeFieldPermission.employee_time_off));

  const canViewPerformance =
    isFeatureEnabled(ApiFeatureType.talent_performance_reviews) &&
    canIEdit(ApiPermission.performance_reviews_module);

  const { data: timeOffCategories } = useEmployeeTimeOffCategoriesData(
    employee.id,
    timeOffPayload,
    canViewTimeOff
  );

  const { data: reviewParticipantViews } = useSearchEmployeeReviewParticipantViews({
    employeeId: employee.id,
    filter: { page_number: 1 },
    enabled: canViewPerformance,
  });

  const latestCompletedReview = useMemo(() => {
    const items = reviewParticipantViews?.items ?? [];
    const completedItems = items.filter((item) => item.review.date_completed_at);
    if (completedItems.length === 0) return undefined;
    const toTimestamp = (date?: Date | null) => (date ? date.getTime() : 0);

    return completedItems.reduce((latest, current) => {
      const latestDate = toTimestamp(latest.review.date_completed_at);
      const currentDate = toTimestamp(current.review.date_completed_at);

      return currentDate > latestDate ? current : latest;
    });
  }, [reviewParticipantViews?.items]);

  const workInfoItems = useMemo<SummaryRowItem[]>(() => {
    const result: SummaryRowItem[] = [];
    const positionTitle = canViewPosition ? getEmployeePositionTitle(employee) : undefined;

    if (positionTitle) {
      result.push({
        id: "position",
        title: t("core:position"),
        caption: positionTitle,
        emoji: ":briefcase:",
      });
    }

    const employmentTypeTitle = canViewEmploymentType
      ? employee.employee_employment?.employment_type?.title
      : undefined;
    const employmentContractTitle = canViewEmploymentContract
      ? employee.employee_employment?.employment_contract?.title
      : undefined;
    const employmentInfo = [employmentTypeTitle, employmentContractTitle].filter(Boolean);

    if (employmentInfo.length > 0) {
      result.push({
        id: "employment",
        title: t("core:employment"),
        caption: employmentInfo.join(" · "),
        emoji: ":writing_hand:",
      });
    }

    return result;
  }, [employee, canViewEmploymentType, canViewEmploymentContract, canViewPosition, t]);

  const timeOffItems = useMemo<SummaryRowItem[]>(() => {
    if (!canViewTimeOff) return [];

    return (timeOffCategories ?? []).map((category) => ({
      id: `time-off-${category.id}`,
      title: category.title,
      caption: getEmployeeTimeOffCategoryCaption(category, t),
      emoji: category.emoji,
    }));
  }, [canViewTimeOff, timeOffCategories, t]);

  const performanceItems = useMemo<SummaryRowItem[]>(() => {
    if (!canViewPerformance) return [];
    if (!latestCompletedReview) return [];

    const score = latestCompletedReview.scale_score.avg_scale_score;
    const scoreTitle = score !== null && score !== undefined ? score.toFixed(1) : "-";

    return [
      {
        id: `performance-${latestCompletedReview.review.id}`,
        title: scoreTitle,
        caption: latestCompletedReview.review.title,
        emoji: latestCompletedReview.review.emoji || "🟢",
      },
    ];
  }, [canViewPerformance, latestCompletedReview]);

  const showWorkInfo = workInfoItems.length > 0;
  const showCompensation = canViewCompensationSummary;
  const showTimeOff = timeOffItems.length > 0;
  const terminationDate =
    employee.lifecycle_status === ApiEmployeeLifecycleStatus.terminated
      ? employee.date_termination
      : undefined;
  const timeOffCaption = terminationDate
    ? t("time_off:balance_as_of", {
        date:
          terminationDate.getFullYear() === new Date().getFullYear()
            ? renderShortDate(terminationDate)
            : `${renderShortDate(terminationDate)} ${terminationDate.getFullYear()}`,
      })
    : undefined;
  const showPerformance = performanceItems.length > 0;

  if (!showWorkInfo && !showCompensation && !showTimeOff && !showPerformance) return null;

  return (
    <div className={"flex flex-row gap-9 py-0"}>
      <div className={"w-full overflow-hidden"}>
        <Stack direction={"vertical"} gap={"lg"}>
          <Title
            header={t("common:summary")}
            size={"lg"}
            caption={t("common:summary_permissions_description")}
          />

          {showWorkInfo && (
            <SummarySectionCard
              title={t("core:work_info")}
              items={workInfoItems}
              link={`/employees/${employee.id}/${EmployeePageSection.workInfo}`}
              viewFullTitle={t("common:view")}
            />
          )}

          {showCompensation && (
            <EmployeeCompensationSummarySection
              employeeId={employee.id}
              enabled={showCompensation}
            />
          )}

          {showTimeOff && (
            <SummarySectionCard
              title={t("time_off:time_off")}
              caption={timeOffCaption}
              items={timeOffItems}
              link={`/employees/${employee.id}/${EmployeePageSection.timeOff}`}
              viewFullTitle={t("common:view")}
            />
          )}

          {showPerformance && (
            <SummarySectionCard
              title={t("performance_review:performance")}
              items={performanceItems}
              link={`/employees/${employee.id}/${EmployeePageSection.feedbackEvaluation}`}
              viewFullTitle={t("common:view")}
            />
          )}
        </Stack>
      </div>
    </div>
  );
}
